/**
 * AdminDynamicPricingPanel — Envío 4: editor del precio dinámico.
 *
 * GET/PUT sobre admin_settings.pricing_config (solo admin). Validación de
 * plata en el backend: margen ≥ 1.2 (global y overrides), crédito > 0,
 * mínimo ≥ 1. La vista previa calcula 4 ejemplos fijos con la config que se
 * está editando, ANTES de guardar. Flag APAGADO = cobro fijo actual.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, RefreshCw, Save, Plus, X, Coins, Eye, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';
import { usd } from './AdminCostsPanel';

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const MODE_LABEL = {
  exact: 'exacto',
  max: 'reserva máxima',
  fixed: 'fijo',
  blocked: 'SIN PRECIO',
};

function NumField({ label, value, onChange, step = '0.01', min, testid }) {
  return (
    <label className="admin-dp-field">
      <span>{label}</span>
      <input
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </label>
  );
}

function OverridesEditor({ title, hint, rows, onChange, valueLabel, testid }) {
  const entries = Object.entries(rows || {});
  const setEntry = (oldKey, key, val) => {
    const next = {};
    for (const [k, v] of entries) next[k === oldKey ? key : k] = k === oldKey ? val : v;
    onChange(next);
  };
  return (
    <div className="admin-dp-overrides" data-testid={testid}>
      <div className="admin-dp-overrides-head">
        <h5>{title}</h5>
        <button
          type="button"
          onClick={() => onChange({ ...(rows || {}), '': '' })}
          data-testid={`${testid}-add`}
        >
          <Plus size={12} /> Agregar
        </button>
      </div>
      {hint && <p className="admin-costs-note">{hint}</p>}
      {entries.map(([k, v], i) => (
        <div className="admin-dp-override-row" key={i} data-testid={`${testid}-row-${i}`}>
          <input
            type="text"
            placeholder="result_type (ej. seedance_25)"
            value={k}
            onChange={(e) => setEntry(k, e.target.value, v)}
            data-testid={`${testid}-key-${i}`}
          />
          <input
            type="number"
            step="0.1"
            placeholder={valueLabel}
            value={v}
            onChange={(e) => setEntry(k, k, e.target.value)}
            data-testid={`${testid}-val-${i}`}
          />
          <button
            type="button"
            className="admin-costs-expand"
            onClick={() => {
              const next = { ...(rows || {}) };
              delete next[k];
              onChange(next);
            }}
            data-testid={`${testid}-del-${i}`}
          >
            <X size={13} />
          </button>
        </div>
      ))}
      {!entries.length && <p className="admin-costs-note">Sin entradas: se usa el valor global.</p>}
    </div>
  );
}

export default function AdminDynamicPricingPanel() {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState([]);
  const [previewError, setPreviewError] = useState(null);
  const debounce = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getPricingConfig();
      setCfg(res.config);
      setMeta({ updated_by: res.updated_by, updated_at: res.updated_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar la config de precios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Vista previa con la config EN EDICIÓN (debounce 500 ms), antes de guardar.
  useEffect(() => {
    if (!cfg) return undefined;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res = await adminService.previewPricingConfig(cfg);
        setPreview(res.preview || []);
        setPreviewError(null);
      } catch (e) {
        setPreview([]);
        setPreviewError(e.response?.data?.detail || 'Config inválida');
      }
    }, 500);
    return () => clearTimeout(debounce.current);
  }, [cfg]);

  const patch = (p) => setCfg((c) => ({ ...c, ...p }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        ...cfg,
        margin_factor: Number(cfg.margin_factor),
        credit_value_usd: Number(cfg.credit_value_usd),
        min_credits: Number(cfg.min_credits),
        margin_overrides: Object.fromEntries(
          Object.entries(cfg.margin_overrides || {})
            .filter(([k]) => k.trim())
            .map(([k, v]) => [k.trim(), Number(v)])),
        max_seconds: Object.fromEntries(
          Object.entries(cfg.max_seconds || {})
            .filter(([k]) => k.trim())
            .map(([k, v]) => [k.trim(), Number(v)])),
        typical_seconds: Object.fromEntries(
          Object.entries(cfg.typical_seconds || {})
            .filter(([k]) => k.trim())
            .map(([k, v]) => [k.trim(), Number(v)])),
      };
      const res = await adminService.savePricingConfig(clean);
      setCfg(res.config);
      toast.success('Config guardada');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar la config');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !cfg) {
    return (
      <div className="admin-costs-loading" data-testid="dp-loading">
        <Loader2 size={18} className="animate-spin" /> Cargando la config…
      </div>
    );
  }
  if (!cfg) return null;

  return (
    <div className="admin-dp" data-testid="dynamic-pricing-panel">
      <div className="admin-dp-main">
        <label className="admin-dp-switch" data-testid="dp-enabled-wrap">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
            data-testid="dp-enabled"
          />
          <span>
            Precio dinámico {cfg.enabled ? 'PRENDIDO' : 'apagado'}
            <em>{cfg.enabled
              ? 'los nodos cobran costo real × margen'
              : 'los nodos cobran el fijo de credit_costs (comportamiento actual)'}</em>
          </span>
        </label>

        <div className="admin-dp-fields">
          <NumField label="Margen global (×)" value={cfg.margin_factor} min="1.2" step="0.1"
            onChange={(v) => patch({ margin_factor: v })} testid="dp-margin" />
          <NumField label="Valor del crédito (USD)" value={cfg.credit_value_usd} min="0.000001" step="0.01"
            onChange={(v) => patch({ credit_value_usd: v })} testid="dp-credit-value" />
          <NumField label="Mínimo de créditos" value={cfg.min_credits} min="1" step="1"
            onChange={(v) => patch({ min_credits: v })} testid="dp-min-credits" />
          <label className="admin-costs-check">
            <input
              type="checkbox"
              checked={!!cfg.block_unpriced}
              onChange={(e) => patch({ block_unpriced: e.target.checked })}
              data-testid="dp-block-unpriced"
            />
            Bloquear combinaciones sin precio (402)
          </label>
        </div>

        <OverridesEditor
          title="Margen por tipo de nodo"
          hint="Pisa el margen global para ese result_type. Mínimo 1.2."
          rows={cfg.margin_overrides}
          onChange={(v) => patch({ margin_overrides: v })}
          valueLabel="margen ×"
          testid="dp-overrides"
        />
        <OverridesEditor
          title="Reserva máxima (segundos) por tipo"
          hint="Tipos cuya duración no se conoce antes de generar: se reserva este máximo y al cerrar se cobra lo real."
          rows={cfg.max_seconds}
          onChange={(v) => patch({ max_seconds: v })}
          valueLabel="segundos"
          testid="dp-max-seconds"
        />
        <OverridesEditor
          title="Segundos típicos (GPU/CPU)"
          hint="Modelos cobrados por segundo de cómputo sin métrica previa."
          rows={cfg.typical_seconds}
          onChange={(v) => patch({ typical_seconds: v })}
          valueLabel="segundos"
          testid="dp-typical-seconds"
        />

        <div className="admin-dp-actions">
          <button onClick={save} disabled={saving} data-testid="dp-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Guardar
          </button>
          <button className="admin-refresh" onClick={load} data-testid="dp-reload">
            <RefreshCw size={13} /> Descartar cambios
          </button>
          <span className="admin-costs-note" data-testid="dp-meta">
            <History size={11} /> Último cambio: {meta.updated_by || '—'} · {fmtWhen(meta.updated_at)}
          </span>
        </div>
      </div>

      <div className="admin-dp-preview" data-testid="dp-preview">
        <h5><Eye size={13} /> Vista previa (sin guardar)</h5>
        {previewError && (
          <p className="admin-dp-preview-error" data-testid="dp-preview-error">{previewError}</p>
        )}
        <table className="admin-costs-table">
          <thead>
            <tr><th>Ejemplo</th><th>Modo</th><th>Créditos</th><th>Costo USD</th></tr>
          </thead>
          <tbody>
            {preview.map((r) => (
              <tr key={r.label} data-testid={`dp-preview-${r.result_type}-${r.label}`}>
                <td>{r.label}</td>
                <td>
                  <span className={`admin-costs-tag ${r.mode === 'blocked' ? 'no_price' : ''}`}>
                    {MODE_LABEL[r.mode] || r.mode}
                  </span>
                </td>
                <td data-testid={`dp-preview-credits-${r.label}`}>
                  <Coins size={10} /> {r.credits}
                </td>
                <td>{usd(r.cost_usd)}</td>
              </tr>
            ))}
            {!preview.length && !previewError && (
              <tr><td colSpan={4}><Loader2 size={13} className="animate-spin" /> Calculando…</td></tr>
            )}
          </tbody>
        </table>
        <p className="admin-costs-note">
          Se recalcula sola al editar. Los créditos de la vista previa son los
          que vería el usuario hoy con esta config.
        </p>
      </div>
    </div>
  );
}
